import { LifecycleReason } from "@/components/iris";
import {
  useGroupDashboard,
  useGroupSessions,
  useSessionObjects,
  useSessionsOverview,
} from "@/hooks/use-fleet";
import { fmtLocalDateTimeWithZone } from "@/lib/group-created";
import { Route } from "@/routes/_authed/group-dashboard.$groupId";
import { useState } from "react";
import { GroupSessionsTable } from "./group-sessions-table";
import { SessionFolderCompare } from "./session-folder-compare";
import { SessionsBand } from "./sessions-band";

/**
 * IR-1548: the full-page group dashboard.
 *
 * The group's own state on top, then every scraping session that touched it.
 * Reached from the group name on the Groups tab; the side drawer links here
 * rather than duplicating these fields.
 *
 * ⚠️ Two limits the screen must state rather than imply:
 *   - the folders are DELETED at 30 days, so an old row is trail-only. Silence
 *     about that reads as data loss.
 *   - the index begins 2026-09-03. Nothing before it will ever appear.
 */
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase text-[var(--iris-fg-3)]">{label}</div>
      <div className="text-sm">{value ?? "--"}</div>
    </div>
  );
}

// IR-1425 r12b (Rotem 2026-10-12, comment 64191): every date on this page is
// the viewer's local time with the zone printed beside it (was unlabelled UTC).
// Null / unparseable keeps this page's "--" convention.
function fmt(v: string | null | undefined): string {
  const out = fmtLocalDateTimeWithZone(v);
  return out === "\u2014" ? "--" : out;
}

export function GroupDashboard() {
  const { groupId } = Route.useParams();
  const [env, setEnv] = useState<"prod" | "dev" | "both">("both");
  const [page, setPage] = useState(0);
  // The open thing is a SESSION — a cycle_id — not one environment's row. Prod
  // and dev are twins and expand together, below both.
  const [openCycleId, setOpenCycleId] = useState<string | null>(null);

  const dash = useGroupDashboard(groupId);
  const sessions = useGroupSessions(groupId, env, page);
  // Deliberately NOT passed `env`. The band charts the whole index, both
  // environments, because its subject is the difference between them — a band
  // narrowed to one would show a divergence headline with nothing to compare
  // while still printing a number. It says so on screen.
  const overview = useSessionsOverview(groupId);

  const d = dash.data;
  const rows = sessions.data?.results ?? [];

  // IR-1548: an expanded session must not outlive its row.
  //
  // `openRow` used to be cleared nowhere. Change the environment filter or turn
  // the page with a row open and the panel kept rendering the folder contents
  // of a session no longer in the table, while useSessionObjects kept querying
  // for it — and nothing on screen was marked open, because the row it belonged
  // to was gone.
  //
  // Derived from `rows` rather than cleared by an effect per control: env and
  // page are two of the things that change what is listed, and a refetch or a
  // filter added later would be a third. One rule covers all of them, and it
  // cannot be forgotten when the next control is added.
  //
  const liveOpenCycleId =
    openCycleId && rows.some((r) => r.cycle_id === openCycleId) ? openCycleId : null;

  // BOTH environments, because the panel's subject is the difference between
  // them. Addressed by cycle_id directly rather than by reading the twin row,
  // so a pair whose halves straddle a page boundary still compares correctly —
  // the backend paginates offset+limit per environment, so that happens.
  const prodObjects = useSessionObjects(groupId, liveOpenCycleId, "prod");
  const devObjects = useSessionObjects(groupId, liveOpenCycleId, "dev");

  const hasEnv = (env: string) => rows.some((r) => r.cycle_id === liveOpenCycleId && r.env === env);

  /**
   * The expanded folder, rendered INSIDE the table directly beneath the pair it
   * belongs to. It used to sit after the entire table, so with fifty rows on
   * screen the payload appeared hundreds of pixels below the row that opened
   * it. The 36px indent is the nesting cue the Avatars expander uses.
   */
  function renderFolder(cycleId: string) {
    const loading = prodObjects.isLoading || devObjects.isLoading;
    // A side is absent when the session did not run there at all. That is
    // different from a fetch that failed or a folder past retention, and the
    // panel must not present the three as one thing.
    const prodList = hasEnv("prod") ? (prodObjects.data?.objects ?? null) : null;
    const devList = hasEnv("dev") ? (devObjects.data?.objects ?? null) : null;
    const truncated = prodObjects.data?.truncated || devObjects.data?.truncated;

    if (loading) {
      return <div className="px-9 py-3 text-sm">Reading folders…</div>;
    }

    return (
      <div>
        <SessionFolderCompare cycleId={cycleId} prod={prodList} dev={devList} />
        {truncated && (
          <div className="px-9 pb-3 text-xs text-[var(--iris-neon-orange)]">
            One of these folders holds more than the 500 objects the endpoint returns — the counts
            above are a floor, not a total.
          </div>
        )}
      </div>
    );
  }
  const total = sessions.data?.total ?? 0;

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold">{d?.identity.name ?? "Group"}</h1>
        <div className="text-xs text-[var(--iris-fg-3)] font-mono">{d?.identity.scrapping_id}</div>
      </header>

      {dash.isError && (
        <div className="text-[var(--iris-neon-red)]">Could not load this group's summary.</div>
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* IR-1425 r10 (Rotem, ROUNDS 4): "Lifecycle" is the matrix LCP with
            its reason on a second line; "Collection" is the matrix axis and
            falls back to today's `status.collection` on a pre-port backend. */}
        <Field
          label="Lifecycle"
          value={
            d?.status.lcp_label ? (
              <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
                <span>{d.status.lcp_label}</span>
                <LifecycleReason
                  lcp={d.status.lcp}
                  lcp_reason={d.status.lcp_reason}
                  lcp_reason_label={d.status.lcp_reason_label}
                  lcp_source={d.status.lcp_source}
                  lcp_set_at={d.status.lcp_set_at}
                  lcp_avatar_note={d.status.lcp_avatar_note}
                />
              </span>
            ) : (
              "—"
            )
          }
        />
        <Field label="Collection" value={d?.status.collection_axis_label ?? d?.status.collection} />
        <Field label="Platform" value={d?.identity.platform} />
        <Field label="Group type" value={d?.identity.group_type} />
        <Field label="Messages" value={d?.identity.message_count} />
        <Field
          label="Members"
          value={
            d?.identity.roster_visibility === "hidden"
              ? `${d?.identity.declared_member_count ?? "--"} (declared)`
              : d?.identity.member_count
          }
        />
        {/* The gap this page closes: the card shows a bare avatar COUNT. */}
        <Field
          label="Collecting avatar"
          value={
            d?.avatar
              ? `${d.avatar.name}${d.avatar.status ? ` — ${d.avatar.status}` : ""}`
              : "none recorded"
          }
        />
        {/* IR-1425 r12b: the COLLECTOR's two stamps — its latest attempt (failed
            included) and the last data it delivered with the kind — above the
            three ingestion-side dates that follow. "--" until the r12b backend
            serves the keys on this environment. */}
        <Field label="Last monitoring (collector)" value={fmt(d?.dates.last_attempt_at)} />
        <Field
          label="Last collection"
          value={
            d?.dates.last_collection_at
              ? `${fmt(d.dates.last_collection_at)}${
                  d.dates.last_collection_kind_label
                    ? ` · ${d.dates.last_collection_kind_label}`
                    : ""
                }`
              : "--"
          }
        />
        <Field label="Last message" value={fmt(d?.dates.last_message_at)} />
        <Field label="Last scrape" value={fmt(d?.dates.last_scrape_at)} />
        <Field label="Last ingestion" value={fmt(d?.dates.last_ingested_at)} />
      </section>

      <section>
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-lg">Scraping sessions</h2>
          <select
            aria-label="Environment"
            value={env}
            onChange={(e) => {
              setEnv(e.target.value as "prod" | "dev" | "both");
              setPage(0);
            }}
            className="bg-[var(--iris-bg-2)] text-sm px-2 py-1"
          >
            <option value="both">prod + dev</option>
            <option value="prod">prod</option>
            <option value="dev">dev</option>
          </select>
          <span className="text-xs text-[var(--iris-fg-3)]">{total} total</span>
        </div>

        <p className="text-xs text-[var(--iris-fg-3)] mb-2">
          Folders are deleted 30 days after collection — older rows are a record that collection
          happened, not openable evidence. The index begins 2026-09-03.
        </p>

        {/* Above the rows, because it answers "where is the problem" and the
            rows answer "what happened here". Reading the second first means
            paging through 138 sessions to find the first. */}
        {overview.isError ? (
          <div className="text-xs text-[var(--iris-neon-orange)] mb-3">
            Could not load the session statistics — the table below is unaffected.
          </div>
        ) : (
          overview.data && (
            <SessionsBand
              sessions={overview.data.results}
              truncated={overview.data.truncated}
              envsRead={overview.data.envs_read}
              undated={overview.data.undated}
            />
          )
        )}

        {sessions.isError ? (
          <div className="text-[var(--iris-neon-red)]">
            Could not load sessions for this environment.
          </div>
        ) : (
          <GroupSessionsTable
            rows={rows}
            expandedCycleId={liveOpenCycleId}
            onToggle={(cycleId) => setOpenCycleId(openCycleId === cycleId ? null : cycleId)}
            renderDetail={renderFolder}
          />
        )}

        <div className="flex gap-2 mt-3">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="text-sm disabled:opacity-40"
          >
            previous
          </button>
          <button
            type="button"
            disabled={(page + 1) * 50 >= total}
            onClick={() => setPage((p) => p + 1)}
            className="text-sm disabled:opacity-40"
          >
            next
          </button>
        </div>
      </section>
    </div>
  );
}
